// Kartu QR siap cetak (10 x 15 cm) untuk Brides Memory.
// Ubah kalimat di bawah ini kalau ingin tulisan lain di kartu.
export const CARD_TAGLINE = "Scan untuk mengabadikan momen";
export const CARD_SUBLINE = "Ambil fotomu, semua masuk ke satu album";

export const CARD_W = 1200; // 10 cm pada ±300 dpi
export const CARD_H = 1800; // 15 cm pada ±300 dpi

// Warna sama dengan tema website
const INK = "#1C1C1C";
const PAPER = "#F9F8F6";
const LINE = "#E8E3D9";
const MUTE = "#8A8A8A";
const SOFT = "#5C5C5C";

/* ---------- Font: ikut font website (font-serif & font-sans) ---------- */
const familyOf = (cls) => {
  const el = document.createElement("span");
  el.className = cls;
  el.style.cssText = "position:absolute;visibility:hidden";
  el.textContent = "x";
  document.body.appendChild(el);
  const f = getComputedStyle(el).fontFamily;
  el.remove();
  return f;
};

async function loadFonts() {
  const serif = familyOf("font-serif");
  const sans = familyOf("font-sans");
  try {
    await Promise.all([
      document.fonts.load(`300 64px ${serif}`),
      document.fonts.load(`italic 300 64px ${serif}`),
      document.fonts.load(`400 24px ${sans}`),
      document.fonts.load(`500 24px ${sans}`),
    ]);
  } catch {
    /* kalau gagal, pakai font cadangan browser */
  }
  return { serif, sans };
}

/* ---------- Alat gambar ---------- */
function spaced(ctx, text, cx, y, gap) {
  const chars = [...text];
  const widths = chars.map((c) => ctx.measureText(c).width);
  const total = widths.reduce((a, b) => a + b, 0) + gap * (chars.length - 1);
  let x = cx - total / 2;
  ctx.textAlign = "left";
  chars.forEach((c, i) => {
    ctx.fillText(c, x, y);
    x += widths[i] + gap;
  });
  ctx.textAlign = "center";
}

function wrap(ctx, text, maxW) {
  const words = text.split(/\s+/).filter(Boolean);
  const lines = [];
  let cur = "";
  for (const w of words) {
    const t = cur ? `${cur} ${w}` : w;
    if (ctx.measureText(t).width <= maxW || !cur) cur = t;
    else {
      lines.push(cur);
      cur = w;
    }
  }
  if (cur) lines.push(cur);
  return lines;
}

// cari ukuran font terbesar yang muat maksimal `maxLines` baris
function fitLines(ctx, text, maxW, maxLines, from, to, fontOf) {
  let size = from;
  let lines = [];
  for (; size >= to; size -= 4) {
    ctx.font = fontOf(size);
    lines = wrap(ctx, text, maxW);
    if (lines.length <= maxLines && lines.every((l) => ctx.measureText(l).width <= maxW)) break;
  }
  return { size, lines };
}

const corner = (ctx, x, y, dx, dy, len = 54) => {
  ctx.beginPath();
  ctx.moveTo(x + dx * len, y);
  ctx.lineTo(x, y);
  ctx.lineTo(x, y + dy * len);
  ctx.stroke();
};

/* ---------- Gambar kartu ---------- */
export function drawCard(ctx, o) {
  const { title, subtitle, dateText, qrCanvas, fonts, host } = o;
  const W = CARD_W;
  const H = CARD_H;
  const cx = W / 2;
  const serif = (w, s, italic) => `${italic ? "italic " : ""}${w} ${s}px ${fonts.serif}`;
  const sans = (w, s) => `${w} ${s}px ${fonts.sans}`;

  ctx.fillStyle = PAPER;
  ctx.fillRect(0, 0, W, H);
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";

  // bingkai ganda
  ctx.strokeStyle = INK;
  ctx.lineWidth = 3;
  ctx.strokeRect(56, 56, W - 112, H - 112);
  ctx.strokeStyle = LINE;
  ctx.lineWidth = 2;
  ctx.strokeRect(78, 78, W - 156, H - 156);

  // nama pengantin / acara (tanpa tulisan "DIRGA" di kepala kartu)
  let y = 300;
  const name = fitLines(ctx, title, 900, 2, 120, 44, (s) => serif(300, s));
  ctx.fillStyle = INK;
  ctx.font = serif(300, name.size);
  name.lines.forEach((l, i) => ctx.fillText(l, cx, y + i * name.size * 1.05));
  y += (name.lines.length - 1) * name.size * 1.05;

  if (subtitle) {
    y += 78;
    const sub = fitLines(ctx, subtitle, 880, 2, 50, 34, (s) => serif(300, s, true));
    ctx.fillStyle = SOFT;
    ctx.font = serif(300, sub.size, true);
    sub.lines.forEach((l, i) => ctx.fillText(l, cx, y + i * sub.size * 1.15));
    y += (sub.lines.length - 1) * sub.size * 1.15;
  }

  if (dateText) {
    y += 70;
    ctx.fillStyle = MUTE;
    ctx.font = sans(400, 24);
    spaced(ctx, dateText.toUpperCase(), cx, y, 6);
  }

  // panel QR
  const P = 640;
  const top = Math.min(730, Math.max(640, y + 90));
  const left = (W - P) / 2;
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(left, top, P, P);
  ctx.strokeStyle = LINE;
  ctx.lineWidth = 2;
  ctx.strokeRect(left, top, P, P);
  const pad = 30;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(qrCanvas, left + pad, top + pad, P - pad * 2, P - pad * 2);

  // sudut hiasan di luar panel
  const g = 26;
  ctx.strokeStyle = INK;
  ctx.lineWidth = 4;
  corner(ctx, left - g, top - g, 1, 1);
  corner(ctx, left + P + g, top - g, -1, 1);
  corner(ctx, left - g, top + P + g, 1, -1);
  corner(ctx, left + P + g, top + P + g, -1, -1);

  // ajakan
  const tagY = top + P + 135;
  const tag = fitLines(ctx, CARD_TAGLINE, 940, 1, 62, 40, (s) => serif(300, s, true));
  ctx.fillStyle = INK;
  ctx.font = serif(300, tag.size, true);
  ctx.fillText(tag.lines[0], cx, tagY);
  ctx.fillStyle = SOFT;
  ctx.font = sans(400, 27);
  ctx.fillText(CARD_SUBLINE, cx, tagY + 62);

  // kaki
  ctx.fillStyle = MUTE;
  ctx.font = sans(400, 22);
  spaced(ctx, (host || "").toUpperCase(), cx, H - 128, 8);
}

/* ---------- Hasil akhir: PNG ---------- */
export async function buildCard({ title, subtitle, dateText, qrCanvas }) {
  const fonts = await loadFonts();
  const c = document.createElement("canvas");
  c.width = CARD_W;
  c.height = CARD_H;
  drawCard(c.getContext("2d"), {
    title,
    subtitle,
    dateText,
    qrCanvas,
    fonts,
    host: window.location.hostname.replace(/^www\./, ""),
  });
  return new Promise((res, rej) => c.toBlob((b) => (b ? res(b) : rej(new Error("Gagal membuat kartu"))), "image/png"));
}

export function saveBlob(blob, filename) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
}

// Buka menu bagikan (gambar + teks sekaligus). Kalau perangkat tidak mendukung, kartu diunduh.
export async function shareCard({ blob, filename, text }) {
  const file = new File([blob], filename, { type: "image/png" });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    await navigator.share({ files: [file], text });
    return "shared";
  }
  saveBlob(blob, filename);
  return "saved";
}